import { useCallback } from 'react';
import { setAuditionTracks, setRecitalTrack } from '../../../../services/RepertoireService';
import { fileRef, roleOf, toggledAuditions } from '../../../../services/trackRolesModel';
import { WARMUPS_SONG_ID } from '../../../../services/songNotesModel';

/**
 * Recital / Audition marks for the song tab that is open (2026-09-27). The
 * marks live on the song's repertoire entry (board.currentSong.id); the board
 * is updated first so the badge moves on the tap, then written. A failed
 * write puts the old value back and reports through `onError`; a write that
 * lands reports through `onSuccess`, so the page can clear an earlier error
 * (pass 3 A1: the error used to stay up for the rest of the lesson).
 */
const useTrackRoles = ({ board, uid, onError, onSuccess }) => {
	const song = board && board.currentSong;
	const setSongs = board && board.setSongs;

	const patchSong = useCallback(
		(id, patch) => setSongs && setSongs((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s))),
		[setSongs]
	);

	const setRecital = useCallback(
		async (file) => {
			if (!song || !song.id) return;
			const previous = song.recitalTrack || null;
			const isCurrent = roleOf(file, song) === 'recital';
			const next = isCurrent ? null : { ...fileRef(file), setBy: uid || null, setAt: new Date().toISOString() };
			patchSong(song.id, { recitalTrack: next });
			try {
				await setRecitalTrack(song.id, next ? fileRef(file) : null, previous, uid);
				if (onSuccess) onSuccess();
			} catch (e) {
				patchSong(song.id, { recitalTrack: previous });
				if (onError) onError(e);
			}
		},
		[song, uid, patchSong, onError, onSuccess]
	);

	const toggleAudition = useCallback(
		async (file) => {
			if (!song || !song.id) return;
			const before = song.auditionTracks || [];
			const on = roleOf(file, song) !== 'audition';
			const next = toggledAuditions(before, file, on, { setBy: uid || null, setAt: new Date().toISOString() });
			patchSong(song.id, { auditionTracks: next });
			try {
				await setAuditionTracks(song.id, next);
				if (onSuccess) onSuccess();
			} catch (e) {
				patchSong(song.id, { auditionTracks: before });
				if (onError) onError(e);
			}
		},
		[song, uid, patchSong, onError, onSuccess]
	);

	if (!song || !song.id || song.id === WARMUPS_SONG_ID) return null;
	return { roleOf: (file) => roleOf(file, song), setRecital, toggleAudition };
};

export default useTrackRoles;
